import React, { FC, useState, useEffect, useRef } from "react";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
  Divider,
  CheckBoxWrapper,
  RolesContainer,
} from './styles'
import { icons } from "../../../icons";
import Typography from "../../typography";
import Button from "../../button";
import { useTranslation } from "react-i18next";
import Select from "../../select";
import { HeadLine3 } from "../../typography/styled";
import CheckBox from "../../checkbox";
import { useParams } from "react-router-dom";
import { clientService } from "../../../api";
import { useUi } from "../../../context/ui.context";

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: () => void;
  alertTitle?: string;
  viewModal?: boolean;
}

const AssociateUser: FC<ModalProps> = (props) => {
  const {
    closeModalCancel,
    closeModal,
    alertTitle,
    viewModal,
  } = props
  const [t] = useTranslation()
  const { userId: clientId } = useParams()
  const { viewAlert } = useUi()

  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)

  // Users list and search
  const [users, setUsers] = useState<any[]>([])
  const [usersLoading, setUsersLoading] = useState<boolean>(false)
  const [userSearchString, setUserSearchString] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<any>(null)

  // Roles
  const [availableRoles, setAvailableRoles] = useState<any[]>([])
  const [selectedRoles, setSelectedRoles] = useState<number[]>([])
  const [defaultRoleId, setDefaultRoleId] = useState<number | null>(null)

  const isLoadingRef = useRef(false)
  const initialLoadDone = useRef(false)

  // Load available roles for this client when modal opens
  useEffect(() => {
    if (clientId && viewModal && !initialLoadDone.current) {
      initialLoadDone.current = true
      clientService.getAvailableRoles(clientId)
        .then((res) => {
          setAvailableRoles(res.data.roles || [])
          if (res.data.default_role_id) {
            setDefaultRoleId(res.data.default_role_id)
            setSelectedRoles([res.data.default_role_id])
          }
        })
        .catch((e) => {
          console.error('Failed to load roles', e)
        })
    }
    if (!viewModal) {
      initialLoadDone.current = false
    }
  }, [clientId, viewModal])

  // Load users with search only when modal is open
  useEffect(() => {
    if (!viewModal || !clientId || isLoadingRef.current) return

    isLoadingRef.current = true
    setUsersLoading(true)

    clientService.getUsersForAssociation({
      page: 1,
      page_size: 100,
      search: userSearchString,
      filtering: `clients__id__ne:${clientId}+is_staff:false+is_active:true`
    })
      .then((res) => {
        const usersData = res.data?.objects || []
        const formattedUsers = usersData.map((user: any) => ({
          id: user.id,
          value: user.id,
          label: `${user.full_name || `${user.first_name} ${user.last_name}`} (${user.email})`,
          data: user
        }))
        setUsers(formattedUsers)
      })
      .catch((error) => {
        console.error("Failed to load users", error)
      })
      .finally(() => {
        setUsersLoading(false)
        isLoadingRef.current = false
      })
  }, [userSearchString, clientId, viewModal])

  // Reset state when modal closes
  useEffect(() => {
    if (!viewModal) {
      setSelectedUserId(null)
      setUserSearchString('')
      setSelectedRoles([])
      setUsers([])
    }
  }, [viewModal])

  const editSelectedRoles = (id: number) => {
    const founded = selectedRoles.find((item: number) => item === id)
    if (founded) {
      setSelectedRoles((prev: number[]) => prev.filter((item: number) => item !== id))
    } else {
      setSelectedRoles([...selectedRoles, id])
    }
  }

  const handleAssociate = () => {
    if (!selectedUserId || !clientId) {
      viewAlert({
        severity: 'error',
        message: t("SELECT_USER_REQUIRED")
      })
      return
    }

    setLoading(true)
    clientService.associateUser(clientId, selectedUserId, selectedRoles)
      .then((res) => {
        setLoadingComplete(true)
        viewAlert({
          severity: 'info',
          message: t("USER_ASSOCIATED_SUCCESS")
        })
        setTimeout(() => {
          setLoadingComplete(false)
          setLoading(false)
          closeModal()
        }, 800)
      })
      .catch((e) => {
        setLoading(false)
        setLoadingComplete(false)
        if (e?.response?.data?.detail) {
          viewAlert({
            severity: 'error',
            message: e?.response?.data?.detail
          })
        }
      })
  }

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginTop: '10px', marginLeft: '10px' }}>
          {alertTitle || t("ASSOCIATE_USER")}
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        <Select
          required
          toolTip={false}
          data={users}
          selectedValue={selectedUserId}
          onChange={(id: any) => setSelectedUserId(id)}
          title={t("USER")}
          placeholder={t("SELECT_USER")}
          fieldName='user'
          hasSearch={true}
          onSearchChange={setUserSearchString}
          errors={{}}
          touched={{ user: true }}
        />

        <Divider />

        <Typography variant='body-medium'>
          {t("SELECT_USER_ROLES")}
        </Typography>
        <RolesContainer>
          {availableRoles.map((item: any) => (
            <CheckBoxWrapper key={item.id} onClick={() => editSelectedRoles(item.id)}>
              <CheckBox
                title={item.display_name || item.name}
                value={selectedRoles.includes(item.id)}
                onChange={() => editSelectedRoles(item.id)}
              />
            </CheckBoxWrapper>
          ))}
        </RolesContainer>

        <div style={{ display: 'flex', gap: '6px', marginTop: '30px' }}>
          <Button
            variant="stroke"
            size="display"
            customStyles={{ width: '177px', justifyContent: 'center' }}
            onClick={closeModalCancel}
            title={t("CANCEL")}
          />
          <Button
            variant="primary"
            size="display"
            loading={loading}
            loadingComplete={loadingComplete}
            disabled={!selectedUserId}
            customStyles={{ width: '177px', justifyContent: 'center' }}
            onClick={handleAssociate}
            title={t("ASSOCIATE_USER")}
          />
        </div>
      </BodyModal>
    </Container>
  )
}

export default AssociateUser
